import React, { useState } from 'react';

/* ============================================================================
 * StageGuide — contextual "What to do here" guidance for each workflow stage.
 * Audience: procurement/contracting officers & market researchers (FAR-literate).
 * Tone: concise, action-oriented reminders + FAR basis + what's next. Not a
 * beginner tutorial. Collapsible; dismissal remembered per user in localStorage.
 * ==========================================================================*/

export interface GuideContent {
  title: string;
  purpose: string;        // one line: what this stage is for
  far?: string;           // FAR basis (optional)
  steps: string[];        // key actions the officer should take here
  next?: string;          // what to do after
}

// Keyed by stage/page id. Content grounded in the platform's lifecycle
// (SYSTEM_CAPABILITIES_AND_FLOW): SAM discovery -> Intake (FAR 11) -> Acquisition
// (FAR 12-15) -> Vendor Matching -> Selection/Evaluation -> Award -> Contract -> Closeout.
export const GUIDES: Record<string, GuideContent> = {
  // ── Opportunity workspace tabs ──
  overview: {
    title: 'Overview — Opportunity Record',
    purpose: 'Confirm the core opportunity data before working the lifecycle.',
    steps: [
      'Verify Notice Type, NAICS/PSC, agency, set-aside, and key dates are correct (edit inline; Save when changed).',
      'Confirm the Procurement Case number and any Related Notice are right.',
      'Check the Main POC and financial fields — downstream drafts and the IGCE reference these.',
    ],
    next: 'Move to Intake (FAR Part 11) to describe the requirement.',
  },
  intake: {
    title: 'Intake — Describing the Requirement (FAR Part 11)',
    purpose: 'Define the need so the acquisition is competition-friendly and traceable.',
    far: 'FAR Part 11 (11.002 / 11.101); market research FAR Part 10.',
    steps: [
      'Document the mission need and intended outcome (Section 1).',
      'Record market research (Section 2) — use the Price Benchmark (2b) to inform the IGCE and Save it as the opportunity IGCE.',
      'Define the requirement one of two ways: attach a PWS/SOW/SOO OR write an informal requirement description; the requirement gate flags if neither exists.',
      'Build the Requirements Register (Section 4); keep requirements non-restrictive.',
      'Run AI Assist to draft narrative fields and AI Review for a Part 11 completeness check (advisory).',
    ],
    next: 'Proceed to Acquisition to determine commerciality and select the procedure.',
  },
  acquisition: {
    title: 'Acquisition Path (FAR Part 12–15)',
    purpose: 'Determine commerciality and select the contracting procedure.',
    far: 'FAR 12 (commercial) layers onto Part 13 (SAP), 14 (sealed bidding), or 15 (negotiation).',
    steps: [
      'Set the commerciality determination and estimated value; the system recommends a threshold category and method.',
      'Select the method with rationale (Part 13/14/15); complete the applicable FAR method form.',
      'Avoid Part 14 when discussions or tradeoffs are needed. Use AI Assist/Review for the path.',
    ],
    next: 'Develop the solicitation, then match vendors.',
  },
  draft: {
    title: 'Solicitation Development',
    purpose: 'Generate the market-research or solicitation document for this stage.',
    steps: [
      'Generate a Sources Sought / RFI first for market research, or an RFQ/UCF solicitation when ready.',
      'Ground drafts in the opportunity data, intake, and uploaded documents; edit before use.',
      'Save the draft as a document so it feeds later steps and the record.',
    ],
    next: 'Match vendors against the requirement.',
  },
  matches: {
    title: 'Vendor Matching (FIT)',
    purpose: 'Identify capable vendors for the requirement.',
    steps: [
      'Run Match — vendors are scored on NAICS/PSC fit, past-award and sub-award history, and specialization.',
      'Prioritize vendors with past awards in the opportunity NAICS (exact > 4-digit group > any).',
      'Open a vendor to review Past Awards and Subcontracting history before shortlisting.',
    ],
    next: 'Collect responses, then evaluate and select.',
  },
  evaluate: {
    title: 'Selection & Evaluation',
    purpose: 'Evaluate responses against the requirements and select the awardee.',
    steps: [
      'Add responding vendors, upload their proposals, and run AI evaluation against the requirements (advisory).',
      'Confirm NAICS alignment and price reasonableness against the IGCE / Price Benchmark.',
      'Record the selection rationale; use Finalize Award to open the post-award stage.',
    ],
    next: 'Document and execute the award.',
  },
  contract_admin: {
    title: 'Award, Contract Administration & Closeout',
    purpose: 'Document/execute the award and manage the contract through closeout.',
    far: 'FAR 4.804 (closeout), 42/43/46 (administration), 32 (payment).',
    steps: [
      'Award: complete the verification checklist (SAM, FAPIIS, price reasonableness, funding, responsibility) and notifications.',
      'Contract: track deliverables, invoices, COR activity, and modifications.',
      'Closeout: complete financial closeout, CPARS/performance, property & security, and records/transition.',
    ],
  },
  // ── Standalone pages ──
  'sam-library': {
    title: 'SAM.gov Library',
    purpose: 'Search federal notices and awards for discovery and market research.',
    steps: [
      'Search by keyword or Notice ID; use Advanced Filters (NAICS, PSC, Set-Aside, Federal Organization, Place of Performance, Contract Awardee).',
      'Toggle Opportunities vs Awards to research either notices or historical awards.',
      'Open a record for full detail; promote a notice to a tracked Opportunity to start the lifecycle.',
    ],
  },
  vendors: {
    title: 'Vendors',
    purpose: 'Research the supplier base and each vendor’s federal footprint.',
    steps: [
      'Search by name, UEI, or CAGE; open a vendor for capabilities, Past Awards, and Subcontracting history.',
      'Use Past Awards (prime) and Subcontracting (as a sub) to gauge relevant experience and teaming.',
    ],
  },
  'price-benchmark': {
    title: 'Price Benchmark',
    purpose: 'See what agencies have paid, to support cost estimates (IGCE).',
    steps: [
      'Enter a NAICS or PSC to see the paid distribution (median/percentiles), top agencies, and reference awards.',
      'Use the AI Cost Estimate: describe the work to get a grounded range and reference contracts.',
      'Read percentiles with the sample size (N) and year range — treat as directional, not fixed prices.',
    ],
  },
};

const wrap: React.CSSProperties = {
  background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
  padding: '12px 16px', marginBottom: 16,
};

export default function StageGuide({ id }: { id: string }) {
  const g = GUIDES[id];
  const storeKey = `fti_guide_dismissed_${id}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(storeKey) === '1'; } catch { return false; }
  });

  if (!g) return null;

  if (dismissed) {
    return (
      <div style={{ marginBottom: 12 }}>
        <button
          onClick={() => { try { localStorage.removeItem(storeKey); } catch {} setDismissed(false); }}
          style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '2px 0' }}
          aria-label="Show guidance for this stage"
        >
          ⓘ Show guidance
        </button>
      </div>
    );
  }

  return (
    <div style={wrap} role="note" aria-label="Stage guidance">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>
            <span aria-hidden="true">🧭 </span>{g.title}
          </div>
          <div style={{ fontSize: 12, color: '#1e40af', marginTop: 2 }}>{g.purpose}</div>
        </div>
        <button
          onClick={() => { try { localStorage.setItem(storeKey, '1'); } catch {} setDismissed(true); }}
          style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: 2 }}
          aria-label="Dismiss guidance"
          title="Dismiss (you can reopen it)"
        >×</button>
      </div>

      <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
        {g.steps.map((s, i) => (
          <li key={i} style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{s}</li>
        ))}
      </ul>

      {(g.far || g.next) && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 11 }}>
          {g.far && <span style={{ color: '#6b7280' }}><strong style={{ color: '#374151' }}>FAR basis:</strong> {g.far}</span>}
          {g.next && <span style={{ color: '#6b7280' }}><strong style={{ color: '#374151' }}>Next:</strong> {g.next}</span>}
        </div>
      )}
    </div>
  );
}
